import { useMemo } from 'react'

import { useQuery } from '@connectrpc/connect-query'
import type { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import type {
  DefaultWithdrawOffset,
  MarketOrderPolicyEntry,
} from '@ebalance/proto/bffuser/v1/bid_limit_model_pb'
import { getBidLimitMatrix } from '@ebalance/proto/bffuser/v1/bid_limit_service-BidLimitService_connectquery'

import { type BidLimits, buildBidLimits } from '~/models/bidLimits'

/**
 * settings「入札制限」(bid-limits) の実設定値を BFF `GetBidLimitMatrix` から取得し、
 * (エリア × 売買) 別の解決済み制限 lookup を返す read 専用 hook。
 *
 * AdminBidLimits の `useGetBidLimitMatrix` と同じ generated connect-query
 * (`getBidLimitMatrix`) を直接叩く (= 同一 segment を読む画面どうしで cache を共有)。
 * 入札画面は制限の read のみ必要なため、matrix 編集 (mutation) を持つ AdminBidLimits
 * hook を cross-page import せず、read 専用の薄い wrapper をここに置く。
 * スポット入札画面 (AC-BDL-008) と時間前入札画面 (AC-BDL-014) の両方が使うので、
 * page-local ではなく共有 hook directory に置いている
 * (= frontend/apps/ebalance/CLAUDE.md「複数 page で実際に使われてから共有 directory
 * へ昇格する」)。
 *
 * 取得中 / 取得エラー時も空 lookup を返す (mock フォールバックは持たない)。空 lookup
 * は「制限未設定」 と区別できないため、 呼び出し元は `limits` だけで判断しない:
 * - 提出 / 承認申請のブロック判定は `isLoading || isError` の間 fail-closed にする
 *   (`isError` だけだと初回応答前に「違反なし」 として通ってしまう)。
 * - 入力補助の表示 (BidLimitHint) は `isLoading` / `isError` の間「制限未設定」 と
 *   表示しない。
 *
 * fail-safe: 入札制限は入力補助 (超過警告) でしかないため、取得失敗で error boundary
 * (= 全画面エラー) に飛ばさない。global QueryClient の `throwOnError: true` を本 query
 * だけ `false` に上書きし、失敗は `isError` で呼び出し側に返して画面は描画を継続する。
 */
export function useBidLimits(bidSegment: BidSegment): {
  limits: BidLimits
  isLoading: boolean
  /** 取り直し中を含む取得中。 取り直す前の値で判断してはいけない呼び出し元が見る。 */
  isFetching: boolean
  isError: boolean
  /** 入札制限を取り直す (= 承認時点の設定値で判定し直したい呼び出し元が、 操作の直前に呼ぶ)。 */
  refetch: () => Promise<unknown>
  /**
   * settings「入札制限」のブロック必須化トグルの状態。
   *
   * BFF は `SPOT_BLOCK` を要求した時だけ `block_mandatory_policy` を返す
   * (backend `bidlimit/converter.go` の segment 別 response shape) ため、
   * 他 segment では常に false になる。
   */
  blockMandatoryEnabled: boolean
  /**
   * 「手数料自動考慮」トグルの状態 (`bid-limits.md` §1-3/2-3 AC-BDL-015、
   * SPOT_NORMAL / INTRADAY のみ populated)。 SPOT_BLOCK では常に false
   * (ブロック入札は札作成時の価格入力欄を持たないため対象外、
   * `FeeAutoConsiderPolicy` proto の godoc と同じ理由)。
   */
  feeAutoConsiderEnabled: boolean
  /**
   * 成行注文許可 (AC-BSP-004a、 SoT は `bid-limits.md` §1-1) の (segment, area) 別
   * toggle 一覧。 backend は `SPOT_NORMAL` / `INTRADAY` を要求した時だけ返す
   * (`bidlimit/converter.go`)。 判定は `isMarketOrderAllowed` に通す。
   */
  marketOrderPolicyEntries: readonly MarketOrderPolicyEntry[]
  /** デフォルト取り下げ時刻 (`bid-limits.md` §4)。 INTRADAY 以外・取得前は undefined。 取り直し中は取り直す前の値のままなので、 使う側が `isFetching` / `isError` を見る。 */
  defaultWithdrawOffset: DefaultWithdrawOffset | undefined
} {
  const { data, isLoading, isFetching, isError, refetch } = useQuery(
    getBidLimitMatrix,
    { bidSegment },
    { throwOnError: false },
  )
  const limits = useMemo(
    () => buildBidLimits(data?.cells ?? [], bidSegment),
    [data?.cells, bidSegment],
  )
  return {
    limits,
    isLoading,
    isFetching,
    isError,
    refetch,
    blockMandatoryEnabled: data?.blockMandatoryPolicy?.enabled ?? false,
    feeAutoConsiderEnabled: data?.feeAutoConsiderPolicy?.enabled ?? false,
    marketOrderPolicyEntries: data?.marketOrderPolicyEntries ?? [],
    defaultWithdrawOffset: data?.defaultWithdrawOffsetSetting?.value,
  }
}
